
import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { 
  Card, 
  CardContent,
  CardHeader,
  CardTitle 
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { ItineraryEvent } from '@/types/itinerary';
import { formatDateWithDay } from '@/utils/dateUtils';

interface DailySidebarProps {
  dates: string[];
  selectedDate: string;
  onSelectDate: (date: string) => void;
  events: ItineraryEvent[];
}

const DailySidebarLoading = () => (
  <Card>
    <CardHeader>
      <CardTitle>Trip Days</CardTitle>
    </CardHeader>
    <CardContent>
      <div className="flex justify-center py-4">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-glowingTeal"></div>
      </div>
    </CardContent>
  </Card>
);

const DailySidebarEmpty = () => (
  <Card>
    <CardHeader>
      <CardTitle>Trip Days</CardTitle>
    </CardHeader>
    <CardContent>
      <p className="text-muted-foreground">No trip days available</p>
    </CardContent>
  </Card>
);

const DailySidebar: React.FC<DailySidebarProps> = ({ 
  dates, 
  selectedDate, 
  onSelectDate,
  events
}) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  
  const getEventCountForDate = (date: string) => {
    if (!events || !Array.isArray(events)) return 0;
    
    try {
      return events.filter(event => {
        if (!event?.date) return false;
        
        try {
          // Try to standardize the date format for comparison
          const eventDateString = String(event.date).substring(0, 10); // Get YYYY-MM-DD part
          return eventDateString === date;
        } catch (e) {
          console.error("Error comparing event date:", e, event.date);
          return false;
        }
      }).length;
    } catch (e) {
      console.error("Error counting events:", e);
      return 0;
    }
  };
  
  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };
  
  // If no dates or empty array, show empty state
  if (!dates || dates.length === 0) {
    return <DailySidebarEmpty />;
  }
  
  return (
    <Card className="sticky top-20">
      <CardHeader className="pb-3 flex flex-row items-center justify-between">
        <CardTitle>Trip Days</CardTitle>
        <Button variant="ghost" size="sm" onClick={toggleCollapse} className="h-8 w-8 p-0">
          {isCollapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
        </Button>
      </CardHeader>
      
      {!isCollapsed && (
        <CardContent className="overflow-auto max-h-[calc(100vh-220px)]">
          <div className="flex flex-col space-y-1">
            {dates.map((date) => {
              const isSelected = date === selectedDate;
              const eventCount = getEventCountForDate(date);
              const formattedDate = formatDateWithDay(date);
              
              return (
                <Button
                  key={date}
                  variant="ghost"
                  className={cn(
                    "justify-start h-auto py-2 px-3 text-left w-full",
                    isSelected && "bg-muted font-medium"
                  )}
                  type="button"
                  onClick={() => {
                    console.log(`Date ${date} clicked in DailySidebar, currently selected: ${selectedDate}`);
                    onSelectDate(date);
                  }}
                >
                  <div className="flex flex-col items-start w-full">
                    <span>{formattedDate.day}</span>
                    <span className="text-xs text-muted-foreground flex items-center justify-between w-full">
                      <span>{formattedDate.date}</span>
                      {eventCount > 0 && (
                        <span className="bg-glowingTeal text-white text-xs px-2 py-0.5 rounded-full ml-2">
                          {eventCount}
                        </span>
                      )}
                    </span>
                  </div>
                </Button>
              );
            })}
          </div>
        </CardContent>
      )}
    </Card>
  );
};

export default DailySidebar;
